/**
 * Coupon site range — which Agency subscriptions (by number of sites) a coupon
 * applies to. Either end may be open (null); both open = any number of sites.
 * Bounds come from GET /license/admin/coupons/scope; the backend re-validates.
 */

export interface SiteScope { minSites: number; maxSites: number }

/** Used when GET /coupons/scope fails — matches the Agency slider (1–100 sites). */
export const FALLBACK_SCOPE: SiteScope = { minSites: 1, maxSites: 100 };

/** The saved form, as the API sends and accepts it. */
export interface SiteRange { minSites: number | null; maxSites: number | null }

/** Table label: "Any", "10–50", "10+", "Up to 50". */
export function formatSiteRange(minSites: number | null | undefined, maxSites: number | null | undefined): string {
  const min = minSites ?? null;
  const max = maxSites ?? null;
  if (min === null && max === null) return 'Any';
  if (min !== null && max !== null) return min === max ? String(min) : `${min}–${max}`;
  if (min !== null) return `${min}+`;
  return `Up to ${max}`;
}

/** Editable state of the control. Number fields are strings so they can be cleared while typing. */
export interface SiteRangeDraft {
  mode: 'any' | 'range';
  min: string;
  max: string;
  minOpen: boolean;
  maxOpen: boolean;
}

/** Draft for a saved range (create form: both null → "Any number of sites"). */
export function draftFromRange(range: SiteRange, scope: SiteScope): SiteRangeDraft {
  const { minSites, maxSites } = range;
  if (minSites == null && maxSites == null) {
    return { mode: 'any', min: String(scope.minSites), max: String(scope.maxSites), minOpen: false, maxOpen: false };
  }
  return {
    mode: 'range',
    min: String(minSites ?? scope.minSites),
    max: String(maxSites ?? scope.maxSites),
    minOpen: minSites == null,
    maxOpen: maxSites == null,
  };
}

export type ParsedRange = ({ ok: true } & SiteRange) | { ok: false; error: string };

/** Validate a draft against the scope; returns the API body or a message to show. */
export function parseSiteRange(d: SiteRangeDraft, scope: SiteScope): ParsedRange {
  if (d.mode === 'any') return { ok: true, minSites: null, maxSites: null };
  const read = (raw: string, open: boolean, name: string): number | null | string => {
    if (open) return null;
    const n = Number(raw);
    if (raw.trim() === '' || !Number.isInteger(n)) return `${name} sites must be a whole number.`;
    if (n < scope.minSites || n > scope.maxSites) {
      return `${name} sites must be between ${scope.minSites} and ${scope.maxSites}.`;
    }
    return n;
  };
  const min = read(d.min, d.minOpen, 'Minimum');
  if (typeof min === 'string') return { ok: false, error: min };
  const max = read(d.max, d.maxOpen, 'Maximum');
  if (typeof max === 'string') return { ok: false, error: max };
  if (min !== null && max !== null && min > max) {
    return { ok: false, error: 'Minimum sites cannot be more than maximum sites.' };
  }
  return { ok: true, minSites: min, maxSites: max };
}
